import { Component, Inject, OnInit } from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormArray,
  ValidationErrors,
  Validators,
} from "@angular/forms";

import { ActivatedRoute, Router } from "@angular/router";
import { ApiService } from "src/app/shared/services/api.service";
import { IndustryTypeService } from "src/app/shared/services/industry-type-services/industry-type.service";
import { ToastrMsgService } from "src/app/shared/services/shared-services/toastr.service";
import { UserService } from "src/app/shared/services/user-services/user.service";

/** Same as signup: at least one domain when using multi-select */
function domainSelectionRequired(control: AbstractControl): ValidationErrors | null {
  const v = control.value;
  if (!v || !Array.isArray(v) || v.length === 0) {
    return { required: true };
  }
  return null;
}

@Component({
  selector: "app-add-organization",
  templateUrl: "./add-organization.component.html",
  styleUrls: ["./add-organization.component.css"],
})
export class AddOrganizationComponent {
  orgForm!: FormGroup;
  isSuperAdmin = false;
  inviteMode = false;
  inviteForm!: FormGroup;

  machineType: any;
  errorMessage1: any;

  submitted = false;
  imageurl: any;
  file: any;

  companyForm: FormGroup;
  organizationId: any;

  /** Same as signup-form: country/state/city dropdowns */
  states: string[] = [];
  cities: string[] = [];

  /** Same four domains as `org-organization` subscription edit + `domain-selection`. */
  readonly grantDomainCatalog = [
    { name: "Consultancy" as const },
    { name: "Road" as const },
    { name: "Construction" as const },
    { name: "Utilities" as const },
  ];
  /** Domains that will receive a non-null JSON column on the granted subscription. */
  selectedGrantDomains: string[] = [];
  /** Rolled-up caps shown next to per-domain inputs (mirrors top-level DB fields). */
  grantTotalUsers = 0;
  grantTotalProjects: number | null = null;
  /** Set once the admin manually toggles a grant tile so we stop auto-mirroring org domains. */
  private grantDomainsManuallyTouched = false;

  constructor(
    private formBuilder: FormBuilder,
    private readonly apiService: ApiService,
    private router: Router,
    private route: ActivatedRoute,
    private readonly toastrMsgService: ToastrMsgService
  ) {



  }


  ngOnInit() {
    try {
      const raw = localStorage.getItem("user");
      if (raw) {
        const parsed = JSON.parse(raw);
        const user = parsed?.user || parsed;
        this.isSuperAdmin = user?.type === 1 || user?.role_id === 1 || user?.role_name === "Admin" || user?.role_name === "Super Admin";
      }
    } catch {}

    this.route.params.subscribe((params) => {
      this.organizationId = params['id'];
    });
    if (this.organizationId) {
      this.getOrganizationDetails();
    }
    this.orgForm = this.formBuilder.group({
      org_name: ["", Validators.required],
      org_type: ["", Validators.required],
      registration_number: [""],
      gst_number: ["", [Validators.pattern('^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$')
      ]],
      pan_number: ["", [Validators.pattern('^[A-Z]{5}[0-9]{4}[A-Z]{1}$')
      ]],
      address_line_1: ["", Validators.required],
      address_line_2: ["", Validators.required],
      country: ["", Validators.required],
      state: ["", Validators.required],
      city: ["", Validators.required],
      postal_code: ["", Validators.required],
      primary_contact_name: ["", Validators.required],
      primary_contact_email: ["", [Validators.required, Validators.email]],
      primary_contact_phone: ["", [Validators.required, Validators.pattern(/^[0-9]{10}$/)]],
      onboarding_date: [""],
      status: [true],
      metadata: ["{}"],
      domain: [[], [domainSelectionRequired]],
      membership_start_date: ["", Validators.required],
      membership_end_date: ["", Validators.required],
      grantAccess: this.formBuilder.group({
        enabled: [false],
        // free_user | trial_user | paid_user (stored in DB column subscriptions.subscription_user_type)
        type: ["free_user"],
        durationMonths: [1],
        total_amount: [0],
        membership_start_date: [""],
        membership_end_date: [""],
        // Per-domain user/project caps. Mirrors the tile UX in `org-organization`
        // and the purchase `domain-selection` flow; totals get rolled up into
        // `usersCount` / `projectsCount` (legacy fields) before submit.
        Consultancy: this.formBuilder.group({
          users_count: [1],
          projects_count: [null],
        }),
        Road: this.formBuilder.group({
          users_count: [1],
          projects_count: [null],
        }),
        Construction: this.formBuilder.group({
          users_count: [1],
          projects_count: [null],
        }),
        Utilities: this.formBuilder.group({
          users_count: [1],
          projects_count: [null],
        }),
      }),
      keyContacts: this.formBuilder.array([]),
    });

    this.inviteForm = this.formBuilder.group({
      org_name: ["", Validators.required],
      primary_contact_email: ["", [Validators.required, Validators.email]],
      role: ["Organization Admin", Validators.required],
    });

    this.bindApiErrorReset("gst_number");
    this.bindApiErrorReset("primary_contact_email");

    // Keep grant tiles + totals in sync with form edits.
    this.grantDomainCatalog.forEach((d) => {
      this.orgForm
        .get(["grantAccess", d.name])
        ?.valueChanges.subscribe(() => this.recomputeGrantTotals());
    });
    // Auto-mirror org domain selection into the grant tiles until the admin
    // manually toggles one (then we respect their selection).
    this.orgForm.get("domain")?.valueChanges.subscribe((v: string[] | string) => {
      if (this.grantDomainsManuallyTouched) return;
      const enabled = this.orgForm.get("grantAccess.enabled")?.value === true;
      if (!enabled) return;
      this.mirrorGrantDomainsFromOrg(v);
    });
    // First time Grant Access is enabled, seed the tiles from the org domains.
    this.orgForm.get("grantAccess.enabled")?.valueChanges.subscribe((on: boolean) => {
      if (!on) return;
      if (this.selectedGrantDomains.length === 0 && !this.grantDomainsManuallyTouched) {
        this.mirrorGrantDomainsFromOrg(this.orgForm.get("domain")?.value);
      }
    });
  }

  private mirrorGrantDomainsFromOrg(domainVal: string[] | string | null | undefined): void {
    const allowed = this.grantDomainCatalog.map((d) => d.name as string);
    const arr = Array.isArray(domainVal)
      ? domainVal
      : typeof domainVal === "string"
      ? domainVal.split(",").map((s) => s.trim())
      : [];
    const next = arr.filter((d) => allowed.includes(d));
    this.selectedGrantDomains = next;
    for (const name of next) {
      const g = this.orgForm.get(["grantAccess", name]);
      if (g && (g.value?.users_count == null || g.value?.users_count === "")) {
        g.patchValue({ users_count: 1 }, { emitEvent: false });
      }
    }
    this.recomputeGrantTotals();
  }

  toggleGrantDomain(name: string): void {
    this.grantDomainsManuallyTouched = true;
    const i = this.selectedGrantDomains.indexOf(name);
    if (i >= 0) {
      this.selectedGrantDomains = this.selectedGrantDomains.filter((_, j) => j !== i);
    } else {
      this.selectedGrantDomains = [...this.selectedGrantDomains, name];
      this.orgForm
        .get(["grantAccess", name])
        ?.patchValue({ users_count: 1, projects_count: null }, { emitEvent: false });
    }
    this.recomputeGrantTotals();
  }

  isGrantDomainSelected(name: string): boolean {
    return this.selectedGrantDomains.includes(name);
  }

  recomputeGrantTotals(): void {
    let users = 0;
    let projects = 0;
    let anyProject = false;
    for (const name of this.selectedGrantDomains) {
      const v = this.orgForm.get(["grantAccess", name])?.value;
      users += Number(v?.users_count) || 0;
      const p = v?.projects_count;
      if (p !== null && p !== undefined && p !== "" && !isNaN(Number(p))) {
        anyProject = true;
        projects += Number(p);
      }
    }
    this.grantTotalUsers = users;
    this.grantTotalProjects = anyProject ? projects : null;
  }

  /** Build `{ users_count, projects_count? }` or `null` for a domain column. */
  private buildGrantDomainJson(name: string): object | null {
    if (!this.selectedGrantDomains.includes(name)) return null;
    const v = this.orgForm.get(["grantAccess", name])?.value;
    const u = Math.max(0, Number(v?.users_count) || 0);
    const pRaw = v?.projects_count;
    const p =
      pRaw === null || pRaw === undefined || pRaw === ""
        ? null
        : Math.max(0, Number(pRaw));
    const o: Record<string, number> = { users_count: u };
    if (p !== null && !isNaN(p)) o["projects_count"] = p;
    return o;
  }

  get keyContacts(): FormArray {
    return this.orgForm.get("keyContacts") as FormArray;
  }

  addKeyContact() {
    const contactGroup = this.formBuilder.group({
      name: ["", Validators.required],
      email: ["", [Validators.required, Validators.email]],
      role: ["", Validators.required],
      mobile: ["", [Validators.required, Validators.pattern(/^[0-9]{10}$/)]],
    });
    this.keyContacts.push(contactGroup);
  }

  removeContact(index: number) {
    this.keyContacts.removeAt(index);
  }

  /** Align with signup-form.component.ts */
  onCountryChange(): void {
    this.apiService.getstates().subscribe({
      next: (res: any) => {
        this.states = Array.isArray(res) ? res : [];
        this.cities = [];
        this.orgForm.patchValue({ state: "", city: "" });
      },
      error: (err) => {
        console.error(err);
        this.toastrMsgService.error(err?.error?.errorMessage || "Failed to load states");
      },
    });
  }

  onStateChange(): void {
    const state = this.orgForm.value.state;
    if (!state) {
      this.cities = [];
      this.orgForm.patchValue({ city: "" });
      return;
    }
    this.apiService.getdistricts(state).subscribe({
      next: (res: any) => {
        this.cities = Array.isArray(res) ? res : [];
        this.orgForm.patchValue({ city: "" });
      },
      error: (err) => {
        console.error(err);
        this.toastrMsgService.error(err?.error?.errorMessage || "Failed to load cities");
      },
    });
  }

  private loadStatesAndCitiesForEdit(country?: string, state?: string, city?: string): void {
    if (!country) {
      this.states = [];
      this.cities = [];
      return;
    }
    this.apiService.getstates().subscribe({
      next: (res: any) => {
        this.states = Array.isArray(res) ? res : [];
        if (!state) {
          this.cities = [];
          return;
        }
        this.apiService.getdistricts(state).subscribe({
          next: (d: any) => {
            this.cities = Array.isArray(d) ? d : [];
            if (city && this.cities.length && !this.cities.includes(city)) {
              this.cities = [...this.cities, city];
            }
          },
          error: () => {
            this.cities = city ? [city] : [];
          },
        });
      },
      error: () => {
        this.states = [];
        this.cities = [];
      },
    });
  }

  handleFileInput(event: any) {
    this.file = event.target.files[0];
    const fileSize = this.file.size / 1024 / 1024; // Convert bytes to KB
    const fileType = this.file.type;

    if (!this.file) {
      // this.errorMessage1 = 'No file chosen.';
      return;
    }

    if (fileType !== "image/jpeg" && fileType !== "image/png") {
      this.errorMessage1 = "File should be JPEG or PNG.";
      return;
    }

    if (fileSize > 3) {
      this.errorMessage1 = "File size should be less than 3 MB.";
      return;
    }

    this.imageurl = URL.createObjectURL(this.file);
  }

  onSubmit(): void {
    if (!this.organizationId && this.isSuperAdmin && this.inviteMode) {
      if (this.inviteForm.invalid) {
        this.inviteForm.markAllAsTouched();
        return;
      }
      this.apiService.inviteOrganization(this.inviteForm.value).subscribe({
        next: () => {
          this.toastrMsgService.success("Invite sent successfully");
          this.router.navigate(['/home/customer/organization-list']);
        },
        error: (err) => {
          this.toastrMsgService.error(err?.error?.message || "Failed to invite organization");
        },
      });
      return;
    }

    if (this.organizationId) {
      if (this.orgForm.invalid) {
        this.orgForm.markAllAsTouched();
        return;
      }
      this.update(this.organizationId);
    } else {
      if (this.orgForm.invalid) {
        this.orgForm.markAllAsTouched();
        return;
      }
      if (this.isSuperAdmin && this.orgForm.get("grantAccess.enabled")?.value === true) {
        if (this.selectedGrantDomains.length === 0) {
          this.toastrMsgService.error(
            "Select at least one domain for the granted subscription."
          );
          return;
        }
        for (const name of this.selectedGrantDomains) {
          const u = Number(this.orgForm.get(["grantAccess", name, "users_count"])?.value);
          if (!u || u < 1) {
            this.toastrMsgService.error(
              `Each selected domain needs at least 1 user seat (${name}).`
            );
            return;
          }
        }
      }
      const payload = this.buildOrganizationPayload();
      this.apiService.saveOrganization(payload).subscribe({
        next: (res: any) => {
          this.toastrMsgService.success(res?.message || 'Organization added successfully');
          if (res?.grantedSubscription) {
            this.toastrMsgService.info('Organization onboarded and subscription access granted.');
          }
          this.router.navigate(['/home/customer/organization-list']);
        },
        error: (err) => {
          this.handleOrganizationApiError(err);
        },
        complete: () => console.log("Observable complete"),
      });
    }
  }
  update(id: any): void {
    const payload = this.buildOrganizationPayload();
    this.apiService.updateOrganization(id, payload).subscribe({
      next: (res: any) => {
        this.toastrMsgService.success(res?.message || 'Organization updated successfully');
        this.router.navigate(['home/customer/organization-list']);
      },
      error: (err) => {
        this.handleOrganizationApiError(err);
      },
      complete: () => console.log("Observable complete"),
    });

  }

  private handleOrganizationApiError(err: any): void {
    const apiErr = err?.error || {};
    const field = apiErr?.field as string | undefined;
    const message =
      apiErr?.message ||
      apiErr?.errorMessage ||
      'Failed to process organization request';

    if (field) {
      const control = this.orgForm.get(field);
      if (control) {
        control.setErrors({ ...(control.errors || {}), server: true, apiErrorCode: apiErr?.error || 'API_ERROR' });
        control.markAsTouched();
      }
    }

    this.toastrMsgService.error(message);
  }

  private bindApiErrorReset(controlName: string): void {
    const control = this.orgForm.get(controlName);
    if (!control) return;
    control.valueChanges.subscribe(() => {
      if (!control.hasError("server")) return;
      const { server, apiErrorCode, ...rest } = control.errors || {};
      control.setErrors(Object.keys(rest).length ? rest : null);
    });
  }

  /** Match signup-form: domain is stored as comma-separated string; multi-select uses string[]. */
  private buildOrganizationPayload(): Record<string, unknown> {
    const raw = this.orgForm.getRawValue() as Record<string, any>;
    const domainVal = raw["domain"];
    const domainStr = Array.isArray(domainVal) ? domainVal.join(",") : (domainVal ?? "");
    const { keyContacts: _kc, grantAccess, ...rest } = raw;

    // Build a grantAccess payload that includes BOTH per-domain JSON columns
    // (matches the subscription edit / purchase shape) AND the legacy rolled-up
    // `usersCount` / `projectsCount` totals the server already understands.
    let grantAccessOut: any = grantAccess;
    if (grantAccess && grantAccess.enabled === true) {
      this.recomputeGrantTotals();
      grantAccessOut = {
        enabled: true,
        type: grantAccess.type,
        durationMonths: grantAccess.durationMonths,
        total_amount: grantAccess.total_amount,
        membership_start_date: grantAccess.membership_start_date,
        membership_end_date: grantAccess.membership_end_date,
        usersCount: this.grantTotalUsers || 1,
        projectsCount: this.grantTotalProjects,
        consultancy: this.buildGrantDomainJson("Consultancy"),
        road: this.buildGrantDomainJson("Road"),
        construction: this.buildGrantDomainJson("Construction"),
        utilities: this.buildGrantDomainJson("Utilities"),
      };
    }

    return { ...rest, domain: domainStr, grantAccess: grantAccessOut };
  }

  getOrganizationDetails() {
    this.apiService.getOrganizations({} as any).subscribe({
      next: (res: any) => {
        console.log(res);
        const data = res.data.find((project: any) => project.org_id == this.organizationId);
        if (data && typeof data.domain === "string" && data.domain.length > 0) {
          data.domain = data.domain.split(",").map((s: string) => s.trim()).filter(Boolean);
        } else if (data && !data.domain) {
          data.domain = [];
        }
        this.orgForm.patchValue(data);
        this.loadStatesAndCitiesForEdit(data.country, data.state, data.city);
      },
      error: (err) => {
        console.log(err);
        this.toastrMsgService.error(err.error.errorMessage);
      },
      complete: () => console.log('Observable complete'),
    });
  }
}
